/**
 * PredicateBuilder — a structured editor for a rule's leaf predicate, producing
 * the canonical shape:
 *
 *   { "field": "total_loan_amount", "op": "<=", "value": 3500000 }
 *
 * Field is a free-text autocomplete (with common engine fields suggested),
 * operator is a select, and value is coerced to number / boolean / string (or a
 * list for the in / not_in operators). Anything that isn't a simple leaf
 * predicate (nested and/or groups, extra keys) can't be represented here — the
 * parent RulesScreen detects that and falls back to the raw-JSON editor.
 */
import Autocomplete from "@mui/material/Autocomplete";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";

export const OPERATORS = [
  { value: "==", label: "= equals" },
  { value: "!=", label: "≠ not equals" },
  { value: "<", label: "< less than" },
  { value: "<=", label: "≤ at most" },
  { value: ">", label: "> greater than" },
  { value: ">=", label: "≥ at least" },
  { value: "in", label: "in list" },
  { value: "not_in", label: "not in list" },
  { value: "contains", label: "contains" },
];

const LIST_OPS = new Set(["in", "not_in"]);

// Common sizer-engine fields, surfaced as autocomplete suggestions.
const FIELD_SUGGESTIONS = [
  "total_loan_amount",
  "initial_loan_amount",
  "as_is_ltv",
  "ltc",
  "ltarv",
  "tltc",
  "fico",
  "best_fico",
  "credit_decision_score",
  "experience_score",
  "verified_liquidity",
  "construction_budget",
  "financed_budget_percent",
  "after_repair_value",
  "as_is_value",
  "purchase_price",
  "property_type",
  "property_sqft",
  "units",
  "state",
  "loan_purpose",
  "loan_product_type",
  "term_months",
  "is_foreign_national",
  "borrower_entity_type",
];

/** Coerce a raw string to number / boolean / trimmed string. */
function coerceScalar(raw) {
  const t = String(raw).trim();
  if (t === "") return "";
  if (t === "true") return true;
  if (t === "false") return false;
  if (!Number.isNaN(Number(t))) return Number(t);
  return raw;
}

/** Whether a predicate is a simple leaf this builder can represent. */
export function isSimpleLeaf(pred) {
  if (!pred || typeof pred !== "object" || Array.isArray(pred)) return false;
  const keys = Object.keys(pred);
  if (keys.length === 0) return true; // empty {} → start fresh
  return keys.every((k) => k === "field" || k === "op" || k === "value");
}

/** Render the stored value back into the text input. */
function valueToText(value) {
  if (value == null) return "";
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
}

const FIELD_LABEL_SX = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "#94a3b8",
  mb: 0.5,
};

export default function PredicateBuilder({ value, onChange }) {
  const pred = value && typeof value === "object" ? value : {};
  const field = pred.field ?? "";
  const op = pred.op ?? "==";
  const isList = LIST_OPS.has(op);

  const emit = (next) => onChange({ field, op, value: pred.value, ...next });

  const onValueChange = (raw) => {
    if (isList) {
      const list = raw
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s !== "")
        .map(coerceScalar);
      emit({ value: list });
    } else {
      emit({ value: coerceScalar(raw) });
    }
  };

  return (
    <Flx ac g={1.25} wrap sx={{ alignItems: "flex-end" }}>
      <Flx column sx={{ flex: "2 1 220px", minWidth: 0 }}>
        <Txt sx={FIELD_LABEL_SX}>Field</Txt>
        <Autocomplete
          freeSolo
          size="small"
          options={FIELD_SUGGESTIONS}
          value={field}
          onChange={(_, v) => emit({ field: v || "" })}
          onInputChange={(_, v) => emit({ field: v || "" })}
          renderInput={(params) => (
            <TextField {...params} placeholder="e.g. total_loan_amount" />
          )}
        />
      </Flx>

      <Flx column sx={{ flex: "1 1 150px", minWidth: 120 }}>
        <Txt sx={FIELD_LABEL_SX}>Operator</Txt>
        <TextField
          select
          size="small"
          value={op}
          onChange={(e) => {
            const nextOp = e.target.value;
            const nowList = LIST_OPS.has(nextOp);
            const wasList = LIST_OPS.has(op);
            let nextVal = pred.value;
            if (nowList && !wasList) nextVal = pred.value == null || pred.value === "" ? [] : [pred.value];
            if (!nowList && wasList) nextVal = Array.isArray(pred.value) ? (pred.value[0] ?? "") : pred.value;
            emit({ op: nextOp, value: nextVal });
          }}
        >
          {OPERATORS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      </Flx>

      <Flx column sx={{ flex: "2 1 200px", minWidth: 0 }}>
        <Txt sx={FIELD_LABEL_SX}>{isList ? "Values (comma-separated)" : "Value"}</Txt>
        <TextField
          size="small"
          value={valueToText(pred.value)}
          onChange={(e) => onValueChange(e.target.value)}
          placeholder={isList ? "CA, TX, FL" : "e.g. 3500000"}
        />
      </Flx>
    </Flx>
  );
}
